/**
 * Copyright Higharc Inc. 2019
 */
import * as THREE from "three";
import { effectComposerHasEnabledPasses, effectComposerRender } from "../examples/jsm/postprocessing/EffectComposer";
import { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import type { AlternativeMaterialPass } from "./AlternativeMaterialPass";
import type { DepthNormalPass } from "./DepthNormalPass";
import type { LineworkShaderUniforms } from "./LineworkShader";
import { LineworkShader } from "./LineworkShader";
import type { RenderTargetPool } from "./RenderTargetPool";
import { screenSpaceViewNormalPrepareShader, screenSpaceViewPositionPrepareShader } from "./screenSpaceHelpersGLSL";

export class LineworkPass extends EffectComposerPass {
  uniforms: LineworkShaderUniforms;

  transparent: boolean;

  private material: THREE.ShaderMaterial;
  private fsQuad: FullScreenQuad;

  constructor(
    public camera: THREE.Camera,
    private depthNormalPass: DepthNormalPass,
    private alternativeMaterialPass: AlternativeMaterialPass,
    private renderTargetPool: RenderTargetPool,
  ) {
    super();

    const shader = LineworkShader;

    this.uniforms = THREE.UniformsUtils.clone(shader.uniforms);
    this.transparent = this.uniforms.transparent.value;

    this.material = new THREE.ShaderMaterial({
      defines: { ...shader.defines },
      uniforms: this.uniforms as any,
      vertexShader: shader.vertexShader,
      fragmentShader: shader.fragmentShader,
      name: "LineworkPass material",
    });

    this.fsQuad = new FullScreenQuad(this.material);
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null) {
    let intermediateBuffer;
    let isCompositing = false;
    if (this.childEffects) {
      isCompositing = effectComposerHasEnabledPasses(this.childEffects);
      if (isCompositing) {
        intermediateBuffer = this.renderTargetPool.reserveRenderTargetMatching(writeBuffer);
        effectComposerRender(this.childEffects, renderer, intermediateBuffer);
        this.material.uniforms["tCompositeSrc"].value = intermediateBuffer.texture;
      }
    }

    screenSpaceViewPositionPrepareShader(this.material, this.camera, this.depthNormalPass, {
      forceRawDepthBuffer: true,
      overrideProjectionMatrix: this.camera.projectionMatrix,
    });
    screenSpaceViewNormalPrepareShader(this.material, this.depthNormalPass);

    this.material.uniforms["tMaterialIdBuffer"].value = this.alternativeMaterialPass.alternativeMaterialTexture;
    this.material.uniforms["isCompositing"].value = isCompositing;
    this.material.uniforms.transparent.value = this.transparent;

    renderer.setRenderTarget(writeBuffer);
    this.fsQuad.render(renderer);
    if (intermediateBuffer) {
      this.renderTargetPool.freeRenderTarget(intermediateBuffer);
    }
  }

  dispose() {
    this.material.dispose();
  }
}
